import { Compass, Lightbulb } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { EmptyStateInsightsPrompt } from '@/components/investigations/EmptyStateInsightsPrompt'
import { InvestigationCanvas } from '@/components/investigations/InvestigationCanvas'
import { InvestigationSidebar } from '@/components/investigations/InvestigationSidebar'
import { ObjectSetChipsBar } from '@/components/investigations/ObjectSetChipsBar'
import { useToolbar } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useGraphViewSidebar } from '@/features/cases/hooks/useGraphViewSidebar'
import { useInvestigateEmptyStateProps } from '@/features/investigations/hooks/useInvestigateEmptyStateProps'
import { useInvestigationUrlSync } from '@/features/investigations/hooks/useInvestigationUrlSync'
import { useObjectSet } from '@/features/investigations/hooks/useObjectSet'
import { useCaseContext } from '@/pages/cases/CaseContext'
import { cn } from '@/utils/cn'

export default function GraphViewsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { caseId } = useCaseContext()
  useInvestigationUrlSync()
  // Default-collapsed so the sidebar doesn't shadow the graph on entry; it
  // auto-opens when the user selects an entity/edge or expands it manually
  // via the canvas handle.
  const { sidebarOpen, setSidebarOpen } = useGraphViewSidebar()
  const objectSet = useObjectSet()
  const showEmptyOverlay = objectSet.ids === null
  const emptyStateProps = useInvestigateEmptyStateProps('graph')

  const toolbarContent = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-7 shrink-0 cursor-pointer gap-1.5 px-2.5 text-xs max-md:h-9 max-md:px-3"
          onClick={() => navigate(`/cases/${caseId}/insights`)}
        >
          <Lightbulb className="size-3.5" />
          {t('investigations.graph.toolbar.insights')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-7 shrink-0 cursor-pointer gap-1.5 px-2.5 text-xs max-md:h-9 max-md:px-3"
          onClick={() => navigate(`/cases/${caseId}/explore-graph`)}
        >
          <Compass className="size-3.5" />
          {t('investigations.graph.toolbar.explore')}
        </Button>
      </div>
    ),
    [navigate, caseId, t]
  )
  useToolbar(toolbarContent)

  // When the sidebar is open, push the cytoscape toolbar (search, layout,
  // …) inward so the sidebar doesn't sit on top of it. Mirrors the Map's
  // toolbar `rightOffset` behaviour: 372px sidebar + 16px gutter + 16px
  // baseline gap = 404px. On the phone the sidebar covers the whole canvas, so
  // the toolbar stays put: the offset only applies from `md` up.
  const pushGraphTools = sidebarOpen && !showEmptyOverlay

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <ObjectSetChipsBar view="graph" />
      <div
        className={cn(
          'relative flex min-h-0 flex-1 flex-col overflow-hidden',
          pushGraphTools && 'md:[--graph-tools-right:404px]'
        )}
      >
        {!showEmptyOverlay && (
          <InvestigationCanvas
            sidebarOpen={sidebarOpen}
            onOpenSidebar={() => setSidebarOpen(true)}
            sidebarSlot={
              <InvestigationSidebar
                caseId={caseId}
                view="graph"
                open={sidebarOpen}
                onOpenChange={setSidebarOpen}
              />
            }
          />
        )}
        {showEmptyOverlay && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-background/40 backdrop-blur-[1px] max-md:p-2">
            <Card
              variant="floating"
              className="pointer-events-auto w-full max-w-5xl p-0 max-md:max-h-full max-md:overflow-y-auto"
            >
              <EmptyStateInsightsPrompt view="graph" {...emptyStateProps} />
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
